import {
  newRelicRUMAccountId,
  newRelicRUMAgentId,
  newRelicRUMApplicationId,
  newRelicRUMBeacon,
  newRelicRUMLicenseKey,
  newRelicRUMTrustKey,
} from '@gonfalon/constants/constants';
import { isNewRelicRumEnabled } from '@gonfalon/dogfood-flags/dogfood-flags';
import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';

/**
 * New Relic Browser (RUM) agent.
 *
 * This runs in parallel with Datadog RUM and LaunchDarkly Observability behind
 * the `enable-web-app-new-relic-rum` flag. The telemetry wrappers in this
 * package dual-write to New Relic via the thin, no-op-safe helpers below, so
 * nothing happens until the agent has been initialized.
 */
let agent: BrowserAgent | undefined;

export function initializeNewRelicRum() {
  if (agent || !isNewRelicRumEnabled()) {
    return;
  }

  const licenseKey = newRelicRUMLicenseKey();
  const applicationID = newRelicRUMApplicationId();
  const beacon = newRelicRUMBeacon() || 'bam.nr-data.net';

  // Without an app id + license key there is nothing to report to.
  if (!licenseKey || !applicationID) {
    return;
  }

  agent = new BrowserAgent({
    // `info` is strictly typed as strings; the loader passes them through.
    info: {
      applicationID,
      licenseKey,
      beacon,
      errorBeacon: beacon,
      sa: 1,
    },
    // `loader_config` is loosely typed and expects numeric ids (matches the
    // copy/paste snippet from the New Relic UI).
    loader_config: {
      accountID: Number(newRelicRUMAccountId()),
      agentID: Number(newRelicRUMAgentId()),
      applicationID: Number(applicationID),
      licenseKey,
      trustKey: Number(newRelicRUMTrustKey()),
    },
    init: {
      // Don't instrument calls to New Relic's own beacon.
      ajax: { deny_list: [beacon] },
      distributed_tracing: { enabled: true },
      performance: { capture_detail: false, capture_marks: false, capture_measures: true },
      privacy: { cookies_enabled: true },
    },
  });
}

export function getNewRelic(): BrowserAgent | undefined {
  return agent;
}

type NewRelicAttributes = Record<string, string | number | boolean>;

/** Record a handled error. Mirrors `datadogRum.addError`. */
export function nrNoticeError(error: Error, customAttributes?: NewRelicAttributes) {
  agent?.noticeError(error, customAttributes);
}

/** Record a custom event/action. Mirrors `datadogRum.addAction`. */
export function nrAddPageAction(name: string, attributes?: NewRelicAttributes) {
  agent?.addPageAction(name, attributes);
}

/** Set a global attribute on subsequent events. Mirrors `datadogRum.setGlobalContextProperty`. */
export function nrSetCustomAttribute(name: string, value: string | number | boolean | null) {
  agent?.setCustomAttribute(name, value);
}

/** Identify the current user. */
export function nrSetUserId(id: string | null) {
  agent?.setUserId(id);
}

/** Name the current SPA route so BrowserInteraction events are attributed correctly. */
export function nrSetCurrentRouteName(name: string) {
  agent?.setCurrentRouteName(name);
}
